import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MagnifyingGlassIcon,
  ClockIcon,
  BanknotesIcon,
  PaperClipIcon,
  PencilIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  CameraIcon,
  DocumentMagnifyingGlassIcon,
  DocumentTextIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  LinkIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { useSearchStore } from '../store/searchStore';
import { useProjectStore } from '../store/projectStore';
import { useClientStore } from '../store/clientStore';
import { useTimeEntryStore } from '../store/timeEntryStore';
import { useExpenseStore } from '../store/expenseStore';
import { useExpenseCategoryStore } from '../store/expenseCategoryStore';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { PdfViewerModal } from '../components/PdfViewerModal';
import {
  TimeEntry,
  TimeEntryCategory,
  Expense,
  ExpenseUpdate,
  Category,
  ProjectSubproject,
  Attachment,
  Link,
  NoteSearchMatch,
  categoriesAPI,
  subprojectsAPI,
  attachmentsAPI,
  dailyNotesAPI,
  linksAPI,
  receiptsAPI,
  PolicyCheckResult,
  expensePoliciesAPI,
} from '../services/api';

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  credit_card: 'Credit Card',
  debit_card: 'Debit Card',
  cash: 'Cash',
  check: 'Check',
  bank_transfer: 'Bank Transfer',
  company_card: 'Company Card',
  personal_card: 'Personal Card',
  other: 'Other',
};

export const SearchPage: React.FC = () => {
  const { t } = useTranslation();
  const {
    timeEntries,
    expenses,
    noteMatches,
    isLoadingTimeEntries,
    isLoadingExpenses,
    isLoadingNotes,
    hasSearched,
    error,
    searchTimeEntries,
    searchExpenses,
    searchNotes,
    clearResults,
    clearError,
  } = useSearchStore();
  const { projects, fetchProjects } = useProjectStore();
  const { clients, fetchClients } = useClientStore();
  const { updateEntry } = useTimeEntryStore();
  const { updateExpense } = useExpenseStore();
  const { categories: expenseCategories, fetchCategories: fetchExpenseCategories } = useExpenseCategoryStore();

  // Search form state
  const [projectId, setProjectId] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSubproject, setFilterSubproject] = useState('');
  const [searchCategories, setSearchCategories] = useState<Category[]>([]);
  const [searchSubprojects, setSearchSubprojects] = useState<ProjectSubproject[]>([]);
  const [searchNotesToggle, setSearchNotesToggle] = useState(false);

  // Result tabs
  type ResultTab = 'timeEntries' | 'notes' | 'expenses';
  const [activeTab, setActiveTab] = useState<ResultTab>('timeEntries');

  // If the user turns off the Notes toggle while viewing the Notes tab, fall back to Time Entries.
  useEffect(() => {
    if (!searchNotesToggle && activeTab === 'notes') {
      setActiveTab('timeEntries');
    }
  }, [searchNotesToggle, activeTab]);

  // Time entry edit modal state
  const [isTimeEntryModalOpen, setIsTimeEntryModalOpen] = useState(false);
  const [editingTimeEntry, setEditingTimeEntry] = useState<TimeEntry | null>(null);
  const [teFormData, setTeFormData] = useState({
    project_id: '',
    description: '',
    hours: '',
    category: '' as TimeEntryCategory | '',
    date: '',
    start_time: '',
    subproject_id: '',
  });
  const [teSubprojectInput, setTeSubprojectInput] = useState('');
  const [teFormError, setTeFormError] = useState('');
  const [teIsSubmitting, setTeIsSubmitting] = useState(false);
  const [projectCategories, setProjectCategories] = useState<Category[]>([]);
  const [subprojects, setSubprojects] = useState<ProjectSubproject[]>([]);

  // Time entry attachment state
  const [modalAttachments, setModalAttachments] = useState<Attachment[]>([]);
  const [isLoadingModalAttachments, setIsLoadingModalAttachments] = useState(false);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);

  // Time entry link state
  const [modalLinks, setModalLinks] = useState<Link[]>([]);
  const [isLoadingModalLinks, setIsLoadingModalLinks] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [newLinkTitle, setNewLinkTitle] = useState('');

  // PDF viewer state
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfViewerUrl, setPdfViewerUrl] = useState('');
  const [pdfViewerFilename, setPdfViewerFilename] = useState('');
  const [pdfViewerAttachment, setPdfViewerAttachment] = useState<Attachment | null>(null);
  const [pdfViewerSource, setPdfViewerSource] = useState<'attachment' | 'other'>('attachment');

  // Expense edit modal state
  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [exFormData, setExFormData] = useState({
    project_id: '',
    expense_category_id: '',
    date: '',
    payee: '',
    amount: '',
    description: '',
    notes: '',
    payment_method: 'credit_card',
    is_reimbursable: true,
  });
  const [exFormError, setExFormError] = useState('');
  const [exIsSubmitting, setExIsSubmitting] = useState(false);
  const [receiptPreview, setReceiptPreview] = useState<string | null>(null);
  const [isUploadingReceipt, setIsUploadingReceipt] = useState(false);
  const [isProcessingOCR, setIsProcessingOCR] = useState(false);
  const [policyWarnings, setPolicyWarnings] = useState<PolicyCheckResult | null>(null);
  const receiptInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchProjects();
    fetchClients();
    fetchExpenseCategories();
  }, [fetchProjects, fetchClients, fetchExpenseCategories]);

  // Load categories and subprojects for search filter dropdowns
  useEffect(() => {
    if (projectId) {
      categoriesAPI.list(projectId).then(setSearchCategories).catch(() => setSearchCategories([]));
      subprojectsAPI.list(projectId).then(setSearchSubprojects).catch(() => setSearchSubprojects([]));
    } else {
      setSearchCategories([]);
      setSearchSubprojects([]);
      setFilterCategory('');
      setFilterSubproject('');
    }
  }, [projectId]);

  // Load categories and subprojects when editing a time entry with a project selected
  useEffect(() => {
    const pid = teFormData.project_id;
    if (pid) {
      categoriesAPI.list(pid).then(setProjectCategories).catch(() => setProjectCategories([]));
      subprojectsAPI.list(pid).then(setSubprojects).catch(() => setSubprojects([]));
    } else {
      setProjectCategories([]);
      setSubprojects([]);
    }
  }, [teFormData.project_id]);

  const canSearch = projectId && searchKeyword.trim();
  const isLoading = isLoadingTimeEntries || isLoadingExpenses || isLoadingNotes;

  // ─── Search handlers ───

  const lastSearchParams = useRef<{
    project_id: string;
    search: string;
    start_date?: string;
    end_date?: string;
    category?: string;
    subproject_id?: string;
    searchNotes?: boolean;
  } | null>(null);

  const handleSearch = async () => {
    if (!canSearch) return;
    clearError();
    const params = {
      project_id: projectId,
      search: searchKeyword.trim(),
      ...(startDate && { start_date: startDate }),
      ...(endDate && { end_date: endDate }),
      ...(filterCategory && { category: filterCategory }),
      ...(filterSubproject && { subproject_id: filterSubproject }),
    };
    lastSearchParams.current = { ...params, searchNotes: searchNotesToggle };
    const tasks: Promise<void>[] = [searchTimeEntries(params), searchExpenses(params)];
    if (searchNotesToggle) {
      tasks.push(
        searchNotes({
          q: searchKeyword.trim(),
          project_id: projectId,
          ...(startDate && { start_date: startDate }),
          ...(endDate && { end_date: endDate }),
          ...(filterCategory && { category: filterCategory }),
          ...(filterSubproject && { subproject_id: filterSubproject }),
        })
      );
    }
    await Promise.all(tasks);
  };

  const refreshSearch = async () => {
    if (lastSearchParams.current) {
      const { searchNotes: wantNotes, ...params } = lastSearchParams.current;
      const tasks: Promise<void>[] = [
        searchTimeEntries(params),
        searchExpenses(params),
      ];
      if (wantNotes) {
        tasks.push(
          searchNotes({
            q: params.search,
            project_id: params.project_id,
            start_date: params.start_date,
            end_date: params.end_date,
            category: params.category,
            subproject_id: params.subproject_id,
          })
        );
      }
      await Promise.all(tasks);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && canSearch) {
      handleSearch();
    }
  };

  const handleClear = () => {
    setProjectId('');
    setSearchKeyword('');
    setStartDate('');
    setEndDate('');
    setFilterCategory('');
    setFilterSubproject('');
    setSearchNotesToggle(false);
    clearResults();
  };

  // Group projects by client for dropdowns
  const projectsByClient = clients
    .filter((c) => c.is_active)
    .map((client) => ({
      client,
      projects: projects.filter((p) => p.client_id === client.id && p.is_active),
    }))
    .filter((group) => group.projects.length > 0);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString();
  };

  // ─── Link handlers ───

  const handleAddLink = async (entryId: string) => {
    if (!newLinkUrl.trim()) return;
    setLinkError(null);
    try {
      const link = await linksAPI.create(entryId, {
        url: newLinkUrl.trim(),
        title: newLinkTitle.trim() || undefined,
      });
      setModalLinks(prev => [...prev, link]);
      setNewLinkUrl('');
      setNewLinkTitle('');
      await refreshSearch();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setLinkError(typeof detail === 'string' ? detail : 'Failed to add link');
    }
  };

  const handleDeleteLink = async (linkId: string, entryId: string) => {
    try {
      await linksAPI.delete(linkId);
      setModalLinks(prev => prev.filter(l => l.id !== linkId));
      await refreshSearch();
    } catch (err) {
      console.error('Failed to delete link:', err);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // ─── Time Entry edit modal handlers ───

  const handleEditTimeEntry = async (entry: TimeEntry) => {
    setEditingTimeEntry(entry);
    setTeFormData({
      project_id: entry.project_id,
      description: entry.description,
      hours: String(entry.hours),
      category: entry.category || '',
      date: entry.date,
      start_time: entry.start_time || '',
      subproject_id: entry.subproject_id || '',
    });
    setTeSubprojectInput(entry.subproject_id || '');
    setTeFormError('');
    setAttachmentError(null);
    setLinkError(null);
    setNewLinkUrl('');
    setNewLinkTitle('');

    // Load attachments and links
    setIsLoadingModalAttachments(true);
    setIsLoadingModalLinks(true);
    try {
      const atts = await attachmentsAPI.list(entry.id);
      setModalAttachments(atts);
    } catch {
      setModalAttachments([]);
    } finally {
      setIsLoadingModalAttachments(false);
    }
    try {
      const lnks = await linksAPI.list(entry.id);
      setModalLinks(lnks);
    } catch {
      setModalLinks([]);
    } finally {
      setIsLoadingModalLinks(false);
    }

    setIsTimeEntryModalOpen(true);
  };

  const handleCloseTimeEntryModal = () => {
    setIsTimeEntryModalOpen(false);
    setEditingTimeEntry(null);
    setModalAttachments([]);
    setModalLinks([]);
    setTeFormError('');
    setAttachmentError(null);
    setLinkError(null);
    setNewLinkUrl('');
    setNewLinkTitle('');
  };

  const handleTimeEntrySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!teFormData.project_id) { setTeFormError('Please select a project'); return; }
    if (!teFormData.category) { setTeFormError('Please select a category'); return; }
    if (!teFormData.description.trim()) { setTeFormError('Please enter a description'); return; }
    const hours = parseFloat(teFormData.hours);
    if (isNaN(hours) || hours <= 0 || hours > 24) { setTeFormError(t('errors.invalidHours')); return; }

    setTeIsSubmitting(true);
    try {
      await updateEntry(editingTimeEntry!.id, {
        project_id: teFormData.project_id,
        date: teFormData.date,
        description: teFormData.description.trim(),
        hours: parseFloat(hours.toFixed(2)),
        category: teFormData.category as TimeEntryCategory,
        start_time: teFormData.start_time || null,
        subproject_id: teSubprojectInput.trim() || null,
      });
      handleCloseTimeEntryModal();
      await refreshSearch();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setTeFormError(detail.map((e: any) => `${e.loc?.join('.')}: ${e.msg}`).join(', ') || t('errors.generic'));
      } else {
        setTeFormError(detail || t('errors.generic'));
      }
    } finally {
      setTeIsSubmitting(false);
    }
  };

  // Attachment handlers
  const handleUploadAttachment = async (entryId: string, files: FileList | null) => {
    if (!files || files.length === 0) return;
    setIsUploadingAttachment(true);
    setAttachmentError(null);
    try {
      for (const file of Array.from(files)) {
        if (file.size > 5 * 1024 * 1024) {
          setAttachmentError(`File "${file.name}" exceeds 5 MB limit`);
          continue;
        }
        const attachment = await attachmentsAPI.upload(entryId, file);
        setModalAttachments((prev) => [...prev, attachment]);
      }
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setAttachmentError(typeof detail === 'string' ? detail : 'Failed to upload file');
    } finally {
      setIsUploadingAttachment(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    try {
      await attachmentsAPI.delete(attachmentId);
      setModalAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
    } catch {
      // silently fail
    }
  };

  const handleDownloadAttachment = async (attachment: Attachment) => {
    try {
      const blob = await attachmentsAPI.download(attachment.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.original_filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // silently fail
    }
  };

  const handleViewPdf = async (attachment: Attachment) => {
    try {
      const blob = await attachmentsAPI.download(attachment.id);
      const url = URL.createObjectURL(blob);
      setPdfViewerUrl(url);
      setPdfViewerFilename(attachment.original_filename);
      setPdfViewerSource('attachment');
      setPdfViewerAttachment(attachment);
      setPdfViewerOpen(true);
    } catch {
      // silently fail
    }
  };

  const handleViewNotePdf = async (match: NoteSearchMatch) => {
    try {
      const blob =
        match.source === 'other'
          ? await dailyNotesAPI.download(match.attachment_id)
          : await attachmentsAPI.download(match.attachment_id);
      const url = URL.createObjectURL(blob);
      const src = match.source === 'other' ? 'other' : 'attachment';
      setPdfViewerUrl(url);
      setPdfViewerFilename(match.original_filename);
      setPdfViewerSource(src);
      // Build a minimal Attachment-shaped object so the viewer can show the panel.
      setPdfViewerAttachment({
        id: match.attachment_id,
        time_entry_id: match.time_entry_id || '',
        user_id: '',
        original_filename: match.original_filename,
        stored_filename: '',
        file_size: match.file_size,
        mime_type: match.mime_type,
        ocr_status: 'completed',
        created_at: '',
        download_url:
          src === 'other'
            ? `/daily-note-files/${match.attachment_id}/download`
            : `/attachments/${match.attachment_id}/download`,
      });
      setPdfViewerOpen(true);
      // Fetch the full transcription (markdown) for the viewer panel.
      try {
        const ocr = src === 'other'
          ? await dailyNotesAPI.getOcr(match.attachment_id)
          : await attachmentsAPI.getOcr(match.attachment_id);
        setPdfViewerAttachment(prev => (prev ? { ...prev, ...ocr } : prev));
      } catch {
        /* leave the snippet-only view */
      }
    } catch {
      // silently fail
    }
  };

  const handleDownloadNoteAttachment = async (match: NoteSearchMatch) => {
    try {
      const blob =
        match.source === 'other'
          ? await dailyNotesAPI.download(match.attachment_id)
          : await attachmentsAPI.download(match.attachment_id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = match.original_filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // silently fail
    }
  };

  const handleClosePdfViewer = () => {
    setPdfViewerOpen(false);
    if (pdfViewerUrl) URL.revokeObjectURL(pdfViewerUrl);
    setPdfViewerUrl('');
    setPdfViewerFilename('');
    setPdfViewerAttachment(null);
  };

  // ─── Expense edit modal handlers ───

  const handleEditExpense = (expense: Expense) => {
    setEditingExpense(expense);
    setExFormData({
      project_id: expense.project_id,
      expense_category_id: expense.expense_category_id || '',
      date: expense.date,
      payee: expense.payee,
      amount: String(expense.amount),
      description: expense.description || '',
      notes: expense.notes || '',
      payment_method: expense.payment_method || 'credit_card',
      is_reimbursable: expense.is_reimbursable,
    });
    setReceiptPreview(expense.receipt_image_url || null);
    setExFormError('');
    setPolicyWarnings(null);
    setIsExpenseModalOpen(true);
  };

  const handleCloseExpenseModal = () => {
    setIsExpenseModalOpen(false);
    setEditingExpense(null);
    setReceiptPreview(null);
    setExFormError('');
    setPolicyWarnings(null);
  };

  const handleExpenseSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!exFormData.project_id) { setExFormError('Please select a project'); return; }
    if (!exFormData.payee.trim()) { setExFormError('Please enter a payee'); return; }
    const amount = parseFloat(exFormData.amount);
    if (isNaN(amount) || amount <= 0) { setExFormError('Please enter a valid amount'); return; }

    setExIsSubmitting(true);
    try {
      const updateData: ExpenseUpdate = {
        project_id: exFormData.project_id,
        expense_category_id: exFormData.expense_category_id || undefined,
        date: exFormData.date,
        payee: exFormData.payee.trim(),
        amount: parseFloat(amount.toFixed(2)),
        description: exFormData.description.trim() || undefined,
        notes: exFormData.notes.trim() || undefined,
        payment_method: exFormData.payment_method,
        is_reimbursable: exFormData.is_reimbursable,
      };
      await updateExpense(editingExpense!.id, updateData);
      handleCloseExpenseModal();
      await refreshSearch();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setExFormError(detail.map((e: any) => `${e.loc?.join('.')}: ${e.msg}`).join(', ') || 'Failed to save expense');
      } else {
        setExFormError(detail || 'Failed to save expense');
      }
    } finally {
      setExIsSubmitting(false);
    }
  };

  const handleAmountBlur = () => {
    const val = parseFloat(exFormData.amount);
    if (!isNaN(val) && val > 0) {
      setExFormData((prev) => ({ ...prev, amount: val.toFixed(2) }));
      // Check expense policies
      if (editingExpense && exFormData.expense_category_id) {
        expensePoliciesAPI.check(val, exFormData.expense_category_id, exFormData.date)
          .then(setPolicyWarnings).catch(() => {});
      }
    }
  };

  const handleReceiptUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editingExpense) return;
    setIsUploadingReceipt(true);
    try {
      const result = await receiptsAPI.upload(file);
      setReceiptPreview(result.thumbnail_url || result.image_url);
      await updateExpense(editingExpense.id, {
        receipt_image_url: result.image_url,
        receipt_thumbnail_url: result.thumbnail_url,
      });
      setEditingExpense({
        ...editingExpense,
        receipt_image_url: result.image_url,
        receipt_thumbnail_url: result.thumbnail_url,
      });
    } catch (err: any) {
      setExFormError(err.response?.data?.detail || 'Failed to upload receipt');
    } finally {
      setIsUploadingReceipt(false);
      if (receiptInputRef.current) receiptInputRef.current.value = '';
    }
  };

  const handleOCR = async () => {
    if (!editingExpense) return;
    setIsProcessingOCR(true);
    try {
      const ocrResult = await receiptsAPI.processOCR(editingExpense.id);
      const extracted = ocrResult.ocr_data?.extracted_fields;
      if (extracted) {
        setExFormData((prev) => ({
          ...prev,
          payee: extracted.payee?.value || prev.payee,
          amount: extracted.total_amount?.value ? String(extracted.total_amount.value) : prev.amount,
          description: extracted.description?.value || prev.description,
        }));
      }
    } catch (err: any) {
      setExFormError(err.response?.data?.detail || 'OCR processing failed');
    } finally {
      setIsProcessingOCR(false);
    }
  };

  const activeCategories = projectCategories.filter((c) => c.is_active);
  const activeExpenseCategories = expenseCategories.filter((c: any) => c.is_active);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {t('search.title')}
        </h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          {t('search.description')}
        </p>
      </div>

      {/* Search Form */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="space-y-4">
          {/* Project Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('search.project')} <span className="text-red-500">*</span>
            </label>
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="">{t('search.selectProject')}</option>
              {projectsByClient.map(({ client, projects: clientProjects }) => (
                <optgroup key={client.id} label={client.name}>
                  {clientProjects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {/* Search Keyword */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('search.keyword')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('search.keywordPlaceholder')}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>

          {/* Optional Filters */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('search.startDate')}
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('search.endDate')}
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('search.category')}
              </label>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                disabled={!projectId}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:opacity-50"
              >
                <option value="">{t('search.allCategories')}</option>
                {searchCategories.map((cat) => (
                  <option key={cat.id} value={cat.name}>{cat.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('search.subproject')}
              </label>
              <select
                value={filterSubproject}
                onChange={(e) => setFilterSubproject(e.target.value)}
                disabled={!projectId}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:opacity-50"
              >
                <option value="">{t('search.allSubprojects')}</option>
                {searchSubprojects.map((sp) => (
                  <option key={sp.id} value={sp.id}>{sp.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Notes Search Toggle */}
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={searchNotesToggle}
              onChange={(e) => setSearchNotesToggle(e.target.checked)}
              className="rounded border-gray-300 text-green-600 focus:ring-green-500 h-4 w-4"
            />
            <DocumentTextIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {t('search.includeNotes', 'Also search notes (OCR\'d attachments)')}
            </span>
          </label>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleSearch}
              disabled={!canSearch || isLoading}
              className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 dark:disabled:bg-gray-600 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <MagnifyingGlassIcon className="h-5 w-5 mr-2" />
              {isLoading ? t('actions.loading') : t('search.searchButton')}
            </button>
            <button
              onClick={handleClear}
              className="inline-flex items-center px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg transition-colors"
            >
              {t('search.clearButton')}
            </button>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Results */}
      {hasSearched && (
        <>
          {/* Result Tabs */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex gap-2" role="tablist" aria-label={t('search.resultTabs', 'Result tabs')}>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'timeEntries'}
                onClick={() => setActiveTab('timeEntries')}
                className={
                  'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ' +
                  (activeTab === 'timeEntries'
                    ? 'border-green-600 text-green-700 dark:text-green-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600')
                }
              >
                <ClockIcon className="h-4 w-4" />
                {t('search.timeEntries')}
                <span className={
                  'ml-1 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium ' +
                  (activeTab === 'timeEntries'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300')
                }>
                  {timeEntries.length}
                </span>
              </button>

              {searchNotesToggle && (
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'notes'}
                  onClick={() => setActiveTab('notes')}
                  className={
                    'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ' +
                    (activeTab === 'notes'
                      ? 'border-green-600 text-green-700 dark:text-green-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600')
                  }
                >
                  <DocumentTextIcon className="h-4 w-4" />
                  {t('search.notesMatches', 'Notes')}
                  <span className={
                    'ml-1 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium ' +
                    (activeTab === 'notes'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300')
                  }>
                    {noteMatches.length}
                  </span>
                </button>
              )}

              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'expenses'}
                onClick={() => setActiveTab('expenses')}
                className={
                  'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ' +
                  (activeTab === 'expenses'
                    ? 'border-green-600 text-green-700 dark:text-green-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600')
                }
              >
                <BanknotesIcon className="h-4 w-4" />
                {t('search.expenses')}
                <span className={
                  'ml-1 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium ' +
                  (activeTab === 'expenses'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300')
                }>
                  {expenses.length}
                </span>
              </button>
            </nav>
          </div>

          {/* Time Entries Results */}
          {activeTab === 'timeEntries' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                <ClockIcon className="h-5 w-5 mr-2" />
                {t('search.timeEntries')} ({timeEntries.length})
              </h2>
            </div>
            <div>
              {isLoadingTimeEntries ? (
                <div className="px-6 py-12 text-center">
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('actions.loading')}</p>
                </div>
              ) : timeEntries.length === 0 ? (
                <div className="px-6 py-12 text-center">
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('search.noTimeEntries')}</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {timeEntries.map((entry) => (
                    <div key={entry.id} className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 group">
                      <div className="flex justify-between items-start">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {entry.description}
                            </p>
                            {(entry.attachment_count ?? 0) > 0 && (
                              <span className="inline-flex items-center gap-0.5 text-xs text-gray-500 dark:text-gray-400">
                                <PaperClipIcon className="h-3.5 w-3.5" />
                                {entry.attachment_count}
                              </span>
                            )}
                            {(entry.link_count ?? 0) > 0 && (
                              <span className="inline-flex items-center gap-0.5 text-xs text-gray-500 dark:text-gray-400">
                                <LinkIcon className="h-3.5 w-3.5" />
                                {entry.link_count}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                            {entry.category}
                            {entry.subproject_name && ` · ${entry.subproject_name}`}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                            {formatDate(entry.date)}
                            {entry.project_name && ` · ${entry.project_name}`}
                          </p>
                        </div>
                        <div className="ml-4 flex-shrink-0 flex items-center gap-3">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            {entry.hours}h
                          </span>
                          <button
                            onClick={() => handleEditTimeEntry(entry)}
                            className="p-1.5 text-gray-400 hover:text-green-600 dark:text-gray-500 dark:hover:text-green-400 opacity-0 group-hover:opacity-100 transition-all rounded hover:bg-gray-100 dark:hover:bg-gray-600"
                            title={t('actions.edit')}
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {timeEntries.length > 0 && (
                <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 rounded-b-xl flex justify-end">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Total: <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">{timeEntries.reduce((sum, e) => sum + e.hours, 0).toFixed(2)}h</span>
                  </span>
                </div>
              )}
            </div>
          </div>
          )}

          {/* Notes Search Results */}
          {activeTab === 'notes' && searchNotesToggle && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                  <DocumentTextIcon className="h-5 w-5 mr-2" />
                  {t('search.notesMatches', 'Notes')} ({noteMatches.length})
                </h2>
              </div>
              <div>
                {isLoadingNotes ? (
                  <div className="px-6 py-12 text-center">
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('actions.loading')}</p>
                  </div>
                ) : noteMatches.length === 0 ? (
                  <div className="px-6 py-12 text-center">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t('search.noNoteMatches', 'No matching notes found')}
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {noteMatches.map((match) => (
                      <div key={match.attachment_id} className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <div className="flex justify-between items-start gap-4">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {match.description}
                            </p>
                            {match.source === 'other' ? (
                              <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                                  {t('search.otherNote', 'Other note')}
                                </span>
                              </p>
                            ) : (
                              <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                                {match.category}
                                {match.subproject_name && ` · ${match.subproject_name}`}
                              </p>
                            )}
                            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                              {formatDate(match.date)}
                              {match.source !== 'other' && ` · ${match.client_name} / ${match.project_name}`}
                            </p>
                            {match.snippet && (
                              <p className="mt-2 text-xs italic text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/40 border-l-2 border-green-500 px-3 py-2 rounded-sm">
                                {match.snippet}
                              </p>
                            )}
                            <div className="mt-2 flex items-center gap-2 text-sm">
                              <PaperClipIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                              {match.mime_type === 'application/pdf' ? (
                                <button
                                  type="button"
                                  onClick={() => handleViewNotePdf(match)}
                                  className="text-blue-600 dark:text-blue-400 hover:underline truncate"
                                  title={t('attachments.view', 'View attachment')}
                                >
                                  {match.original_filename}
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => handleDownloadNoteAttachment(match)}
                                  className="text-blue-600 dark:text-blue-400 hover:underline truncate"
                                  title={t('attachments.download')}
                                >
                                  {match.original_filename}
                                </button>
                              )}
                              <span className="text-xs text-gray-400 flex-shrink-0">
                                ({formatFileSize(match.file_size)})
                              </span>
                              <button
                                type="button"
                                onClick={() => handleDownloadNoteAttachment(match)}
                                className="p-1 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                                title={t('attachments.download')}
                              >
                                <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                          {match.source !== 'other' && match.hours != null && (
                            <div className="ml-4 flex-shrink-0">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                {match.hours}h
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Expenses Results */}
          {activeTab === 'expenses' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                <BanknotesIcon className="h-5 w-5 mr-2" />
                {t('search.expenses')} ({expenses.length})
              </h2>
            </div>
            <div>
              {isLoadingExpenses ? (
                <div className="px-6 py-12 text-center">
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('actions.loading')}</p>
                </div>
              ) : expenses.length === 0 ? (
                <div className="px-6 py-12 text-center">
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('search.noExpenses')}</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {expenses.map((expense) => (
                    <div key={expense.id} className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 group">
                      <div className="flex justify-between items-start">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {expense.payee}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                            {expense.category_name && `${expense.category_name}`}
                            {expense.description && ` · ${expense.description}`}
                          </p>
                          {expense.notes && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                              {expense.notes}
                            </p>
                          )}
                          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                            {formatDate(expense.date)}
                            {expense.project_name && ` · ${expense.project_name}`}
                          </p>
                        </div>
                        <div className="ml-4 flex-shrink-0 flex items-center gap-3">
                          <span className="text-sm font-semibold text-gray-900 dark:text-white">
                            ${expense.amount.toFixed(2)}
                          </span>
                          <button
                            onClick={() => handleEditExpense(expense)}
                            className="p-1.5 text-gray-400 hover:text-green-600 dark:text-gray-500 dark:hover:text-green-400 opacity-0 group-hover:opacity-100 transition-all rounded hover:bg-gray-100 dark:hover:bg-gray-600"
                            title={t('actions.edit')}
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {expenses.length > 0 && (
                <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 rounded-b-xl flex justify-end">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Total: <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">${expenses.reduce((sum, e) => sum + e.amount, 0).toFixed(2)}</span>
                  </span>
                </div>
              )}
            </div>
          </div>
          )}
        </>
      )}

      {/* ─── Time Entry Edit Modal ─── */}
      <Modal
        isOpen={isTimeEntryModalOpen}
        onClose={handleCloseTimeEntryModal}
        title={t('timeEntry.editEntry')}
      >
        <form onSubmit={handleTimeEntrySubmit} className="space-y-4">
          {/* Project */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('timeEntry.selectProject')}
            </label>
            <select
              value={teFormData.project_id}
              onChange={(e) => setTeFormData({ ...teFormData, project_id: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="">Select a project...</option>
              {projectsByClient.map(({ client, projects: cp }) => (
                <optgroup key={client.id} label={client.name}>
                  {cp.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {/* Date and Start Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date</label>
              <input
                type="date"
                value={teFormData.date}
                onChange={(e) => setTeFormData({ ...teFormData, date: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Start Time <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                type="time"
                value={teFormData.start_time}
                onChange={(e) => setTeFormData({ ...teFormData, start_time: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent dark:[color-scheme:dark]"
              />
            </div>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
            <select
              value={teFormData.category}
              onChange={(e) => setTeFormData({ ...teFormData, category: e.target.value as TimeEntryCategory })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="">Select a category...</option>
              {activeCategories.map((cat) => (
                <option key={cat.id} value={cat.name}>{cat.name}</option>
              ))}
            </select>
          </div>

          {/* Subproject */}
          {teFormData.project_id && subprojects.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Subproject <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <select
                value={teSubprojectInput}
                onChange={(e) => setTeSubprojectInput(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">No subproject</option>
                {subprojects.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('timeEntry.description')}
            </label>
            <textarea
              value={teFormData.description}
              onChange={(e) => setTeFormData({ ...teFormData, description: e.target.value })}
              placeholder="What did you work on?"
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          {/* Attachments */}
          {editingTimeEntry && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('attachments.title')} <span className="text-gray-400 font-normal">({t('attachments.maxFiles')})</span>
              </label>
              {isLoadingModalAttachments ? (
                <div className="text-sm text-gray-500 dark:text-gray-400">{t('actions.loading')}</div>
              ) : (
                <div className="space-y-1 mb-2">
                  {modalAttachments.map((att) => (
                    <div key={att.id} className="flex items-center justify-between text-sm bg-gray-50 dark:bg-gray-700 rounded px-3 py-1.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <PaperClipIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <span className="truncate text-gray-700 dark:text-gray-300">{att.original_filename}</span>
                        <span className="text-gray-400 dark:text-gray-500 text-xs flex-shrink-0">({formatFileSize(att.file_size)})</span>
                        {att.ocr_status === 'completed' && (
                          <DocumentTextIcon className="h-3.5 w-3.5 text-green-500 flex-shrink-0" title={t('attachments.ocrComplete', 'Text extracted')} />
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {att.mime_type === 'application/pdf' && (
                          <button type="button" onClick={() => handleViewPdf(att)} className="p-1 text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300" title="View PDF">
                            <EyeIcon className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <button type="button" onClick={() => handleDownloadAttachment(att)} className="p-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300" title={t('attachments.download')}>
                          <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                        </button>
                        <button type="button" onClick={() => handleDeleteAttachment(att.id)} className="p-1 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300" title={t('actions.delete')}>
                          <TrashIcon className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {modalAttachments.length < 5 && (
                <label className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg cursor-pointer transition-colors">
                  <PaperClipIcon className="h-4 w-4" />
                  {isUploadingAttachment ? t('actions.loading') : t('attachments.addFile')}
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf,.docx,.txt"
                    multiple
                    disabled={isUploadingAttachment}
                    onChange={(e) => {
                      handleUploadAttachment(editingTimeEntry.id, e.target.files);
                      e.target.value = '';
                    }}
                  />
                </label>
              )}
              {attachmentError && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">{attachmentError}</p>
              )}
              <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">{t('attachments.allowedTypes')}</p>
            </div>
          )}

          {/* Links Section */}
          {editingTimeEntry && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('links.title')} <span className="text-gray-400 font-normal">({t('links.maxLinks')})</span>
              </label>

              {isLoadingModalLinks ? (
                <div className="text-sm text-gray-500 dark:text-gray-400">{t('actions.loading')}</div>
              ) : (
                <div className="space-y-1 mb-2">
                  {modalLinks.map((link) => (
                    <div key={link.id} className="flex items-center justify-between text-sm bg-gray-50 dark:bg-gray-700 rounded px-3 py-1.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <LinkIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="truncate text-blue-600 dark:text-blue-400 hover:underline"
                          title={link.url}
                        >
                          {link.title || link.url}
                        </a>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDeleteLink(link.id, editingTimeEntry.id)}
                        className="p-1 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 flex-shrink-0"
                        title={t('actions.delete')}
                      >
                        <TrashIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {modalLinks.length < 10 && (
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={newLinkUrl}
                    onChange={(e) => setNewLinkUrl(e.target.value)}
                    placeholder={t('links.urlPlaceholder')}
                    className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                  <input
                    type="text"
                    value={newLinkTitle}
                    onChange={(e) => setNewLinkTitle(e.target.value)}
                    placeholder={t('links.titlePlaceholder')}
                    className="w-1/3 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => handleAddLink(editingTimeEntry.id)}
                    disabled={!newLinkUrl.trim()}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400 rounded-lg transition-colors"
                  >
                    <PlusIcon className="h-4 w-4" />
                    {t('links.addLink')}
                  </button>
                </div>
              )}

              {linkError && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">{linkError}</p>
              )}
            </div>
          )}

          {/* Hours */}
          <Input
            label={t('timeEntry.hours')}
            type="number"
            step="0.01"
            min="0.01"
            max="24"
            value={teFormData.hours}
            onChange={(e) => setTeFormData({ ...teFormData, hours: e.target.value })}
            placeholder="e.g., 1.5"
            error={teFormError}
          />

          <div className="flex justify-end space-x-3 pt-4">
            <Button variant="secondary" onClick={handleCloseTimeEntryModal} type="button">{t('actions.cancel')}</Button>
            <Button type="submit" isLoading={teIsSubmitting}>{t('actions.save')}</Button>
          </div>
        </form>
      </Modal>

      {/* ─── Expense Edit Modal ─── */}
      <Modal
        isOpen={isExpenseModalOpen}
        onClose={handleCloseExpenseModal}
        title={t('expenses.editExpense')}
        size="lg"
      >
        <form onSubmit={handleExpenseSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Project */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Project</label>
              <select
                value={exFormData.project_id}
                onChange={(e) => setExFormData({ ...exFormData, project_id: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">Select a project...</option>
                {projectsByClient.map(({ client, projects: cp }) => (
                  <optgroup key={client.id} label={client.name}>
                    {cp.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
              <select
                value={exFormData.expense_category_id}
                onChange={(e) => setExFormData({ ...exFormData, expense_category_id: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">Select a category...</option>
                {activeExpenseCategories.map((cat: any) => (
                  <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date</label>
              <input
                type="date"
                value={exFormData.date}
                onChange={(e) => setExFormData({ ...exFormData, date: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            {/* Payee */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Payee</label>
              <input
                type="text"
                value={exFormData.payee}
                onChange={(e) => setExFormData({ ...exFormData, payee: e.target.value })}
                placeholder="e.g., Uber, Marriott, Amazon"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Amount ($)</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={exFormData.amount}
                onChange={(e) => setExFormData({ ...exFormData, amount: e.target.value })}
                onBlur={handleAmountBlur}
                placeholder="0.00"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            {/* Payment Method */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Payment Method</label>
              <select
                value={exFormData.payment_method}
                onChange={(e) => setExFormData({ ...exFormData, payment_method: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                {Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            {/* Reimbursable */}
            <div className="flex items-end pb-2">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={exFormData.is_reimbursable}
                  onChange={(e) => setExFormData({ ...exFormData, is_reimbursable: e.target.checked })}
                  className="rounded border-gray-300 text-green-600 focus:ring-green-500 h-4 w-4"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Reimbursable</span>
              </label>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
            <input
              type="text"
              value={exFormData.description}
              onChange={(e) => setExFormData({ ...exFormData, description: e.target.value })}
              placeholder="Brief description of the expense"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
            <textarea
              value={exFormData.notes}
              onChange={(e) => setExFormData({ ...exFormData, notes: e.target.value })}
              placeholder="Additional notes (optional)"
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>

          {/* Receipt */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Receipt</label>
            <div className="flex items-center gap-3">
              <input ref={receiptInputRef} type="file" accept="image/*" capture="environment" onChange={handleReceiptUpload} className="hidden" />
              <button
                type="button"
                onClick={() => receiptInputRef.current?.click()}
                disabled={isUploadingReceipt}
                className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                {isUploadingReceipt ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mr-2" />
                ) : (
                  <CameraIcon className="h-4 w-4 mr-2" />
                )}
                {receiptPreview ? 'Replace Receipt' : 'Upload Receipt'}
              </button>
              {editingExpense?.receipt_image_url && (
                <button
                  type="button"
                  onClick={handleOCR}
                  disabled={isProcessingOCR}
                  className="inline-flex items-center px-3 py-2 border border-blue-300 dark:border-blue-600 rounded-lg text-sm text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                >
                  {isProcessingOCR ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2" />
                  ) : (
                    <DocumentMagnifyingGlassIcon className="h-4 w-4 mr-2" />
                  )}
                  OCR Extract
                </button>
              )}
              {receiptPreview && (
                <div className="relative">
                  <img src={receiptPreview} alt="Receipt" className="h-16 w-16 object-cover rounded-lg border border-gray-200 dark:border-gray-600" />
                  <button type="button" onClick={() => setReceiptPreview(null)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5">
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Policy Warnings */}
          {policyWarnings && policyWarnings.is_over_limit && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
              <div className="flex items-start">
                <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200">Policy Warning</p>
                  {policyWarnings.warnings.map((w: any, i: number) => (
                    <p key={i} className="text-xs text-amber-700 dark:text-amber-300 mt-1">{w.message}</p>
                  ))}
                </div>
              </div>
            </div>
          )}

          {exFormError && (
            <p className="text-sm text-red-600 dark:text-red-400">{exFormError}</p>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <Button variant="secondary" onClick={handleCloseExpenseModal} type="button">{t('actions.cancel')}</Button>
            <Button type="submit" isLoading={exIsSubmitting}>{t('actions.save')}</Button>
          </div>
        </form>
      </Modal>

      {/* PDF Viewer */}
      <PdfViewerModal
        isOpen={pdfViewerOpen}
        onClose={handleClosePdfViewer}
        pdfUrl={pdfViewerUrl}
        filename={pdfViewerFilename}
        onDownload={() => {}}
        ocrText={pdfViewerAttachment?.ocr_text}
        ocrMarkdown={pdfViewerAttachment?.ocr_markdown}
        ocrStatus={pdfViewerAttachment?.ocr_status}
        ocrMethod={pdfViewerAttachment?.ocr_method}
        onRetryOcr={pdfViewerAttachment ? async () => {
          try {
            const api = pdfViewerSource === 'other' ? dailyNotesAPI : attachmentsAPI;
            const result = await api.retryOcr(pdfViewerAttachment.id);
            setPdfViewerAttachment(prev => (prev ? { ...prev, ...result } : prev));
          } catch (err) {
            console.error('OCR re-run failed:', err);
          }
        } : undefined}
        onRefreshOcr={pdfViewerAttachment ? async () => {
          const api = pdfViewerSource === 'other' ? dailyNotesAPI : attachmentsAPI;
          const result = await api.getOcr(pdfViewerAttachment.id);
          setPdfViewerAttachment(prev => (prev ? { ...prev, ...result } : prev));
        } : undefined}
        onSaveCorrection={pdfViewerAttachment ? async (md) => {
          const api = pdfViewerSource === 'other' ? dailyNotesAPI : attachmentsAPI;
          const result = await api.saveCorrection(pdfViewerAttachment.id, md);
          setPdfViewerAttachment(prev => (prev ? { ...prev, ...result } : prev));
        } : undefined}
      />
    </div>
  );
};
